import React, { useContext, useState, useEffect, createContext } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, collection, getDocs } from "firebase/firestore"; // Add collection and getDocs here
import { auth, db, googleProvider } from "../services/firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState("user");
  const [userSettings, setUserSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, displayName) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Update profile with display name
    await updateProfile(auth.currentUser, { displayName });

    // Create comprehensive user document in Firestore
    const userData = {
      uid: userCredential.user.uid,
      email,
      displayName,
      role: "user", // Default role
      createdAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
      photoURL: userCredential.user.photoURL || null,

      // User Settings
      settings: {
        notifications: {
          email: true,
          push: true,
          telegram: false
        },
        privacy: {
          showOnlineStatus: true,
          allowDirectMessages: true
        },
        chat: {
          soundEnabled: true,
          autoScroll: true,
          theme: "light", // light, dark
          fontSize: "medium" // small, medium, large
        }
      },

      // Profile Information
      profile: {
        avatar: userCredential.user.photoURL || null,
        bio: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: "en"
      },

      // Account Status
      status: {
        isActive: true,
        isVerified: false,
        lastSeen: new Date().toISOString()
      }
    };

    await setDoc(doc(db, "users", userCredential.user.uid), userData);

    return userCredential;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function loginWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Check if user document exists
    const userDoc = await getDoc(doc(db, "users", user.uid));

    if (!userDoc.exists()) {
      // Create new user document for Google users
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        role: "user",
        createdAt: new Date().toISOString(),
        lastLoginAt: new Date().toISOString(),
        photoURL: user.photoURL || null,

        settings: {
          notifications: {
            email: true,
            push: true,
            telegram: false
          },
          privacy: {
            showOnlineStatus: true,
            allowDirectMessages: true
          },
          chat: {
            soundEnabled: true,
            autoScroll: true,
            theme: "light",
            fontSize: "medium"
          }
        },

        profile: {
          avatar: user.photoURL || null,
          bio: "",
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: "en"
        },

        status: {
          isActive: true,
          isVerified: false,
          lastSeen: new Date().toISOString()
        }
      };

      await setDoc(doc(db, "users", user.uid), userData);
    } else {
      // Update last login time for existing users
      await updateDoc(doc(db, "users", user.uid), {
        lastLoginAt: new Date().toISOString(),
        "status.lastSeen": new Date().toISOString()
      });
    }

    return result;
  }

  function logout() {
    return signOut(auth);
  }

  async function getUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role);
        setUserSettings(userData.settings || {});
        return userData;
      }
      return null;
    } catch (error) {
      console.error("Error getting user data:", error);
      return null;
    }
  }

  async function updateUserSettings(uid, settingsUpdate) {
    try {
      await updateDoc(doc(db, "users", uid), {
        [`settings.${Object.keys(settingsUpdate)[0]}`]: Object.values(settingsUpdate)[0],
        "status.lastSeen": new Date().toISOString()
      });
      
      // Update local state
      setUserSettings(prev => ({
        ...prev,
        ...settingsUpdate
      }));
    } catch (error) {
      console.error("Error updating user settings:", error);
      throw error;
    }
  }

  async function updateUserProfile(uid, profileUpdate) {
    try {
      const updateData = {};
      Object.keys(profileUpdate).forEach(key => {
        updateData[`${key}`] = profileUpdate[key];
      });
      updateData["status.lastSeen"] = new Date().toISOString();
      
      await updateDoc(doc(db, "users", uid), updateData);
    } catch (error) {
      console.error("Error updating user profile:", error);
      throw error;
    }
  }

  // New function to get all users
  async function getAllUsers() {
    try {
      const usersCollectionRef = collection(db, "users");
      const querySnapshot = await getDocs(usersCollectionRef);
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return usersList;
    } catch (error) {
      console.error("Error getting all users:", error);
      return [];
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const userData = await getUserData(user.uid); // Get user data

        // Only attempt to update if the user document exists
        if (userData) {
          // Update last seen status
          await updateDoc(doc(db, "users", user.uid), {
            "status.lastSeen": new Date().toISOString()
          });
        } else {
          // The user document doesn't exist, this case is handled by signup/loginWithGoogle
          // The comment below is for handling the logged-out state
          // set default values for the user if it doesn't exist here
        }
      } else {
        setUserRole("user");
        setUserSettings(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userSettings,
    signup,
    login,
    loginWithGoogle,
    logout,
    getUserData,
    updateUserSettings,
    updateUserProfile,
    getAllUsers, // Add the new function here
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}